import * as React from 'react'
import styled from 'styled-components'
import NewComment from '../comments/NewComment'
import Comment from '../comments/Comment'
import { VideoData } from '../../../interfaces'
import {
  CreateComment,
  DeleteComment,
  GetVideoComments,
  LikeComment,
  UnlikeComment,
} from '../../../data/Reactions'
import { useAtom } from 'jotai'
import { createMuiTheme, ThemeProvider } from '@material-ui/core'
import { ExpandMoreRounded } from '@material-ui/icons'
import { rwStreamClientAtom } from '../../../atoms/StreamClient'
import { useInfiniteLoadComment } from '../../../hooks/inifiniteLoadComment'
import { LoadingType } from '../../../hooks/infiniteLoadByCursor'
import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'
import { FbEnrichedUser } from '../../../interfaces/User'
import useAuth from '../../../hooks/auth'
import { captureException } from '@sentry/nextjs'

const OuterDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`

const MoreCommentsDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  margin-top: 25px;
`

const Action = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 600;
`

const theme = createMuiTheme({
  palette: {
    type: 'dark',
  },
})

interface Props {
  video: VideoData
  currentIndex: number
  index: number
  updateHeight: () => void
}

export interface EnrichedCommentData {
  user: FbEnrichedUser
  id: string
  created_at: Date
  content: string
  replies_count: number
  likes_count: number
  own: boolean
  liked?: string
  parent_id?: string
}

const CommentsDetails = ({ video, currentIndex, index, updateHeight }: Props): JSX.Element => {
  const { profile } = useAuth()
  const [streamClient] = useAtom(rwStreamClientAtom)

  const getComments = React.useCallback(
    async (
      lastID: string
    ): Promise<{ items: EnrichedCommentData[]; count: number; nextID: string }> => {
      try {
        const feed = streamClient.feed('videos', video.created_by)
        const query = await feed.get({
          limit: 1,
          id_gte: video.activity_id,
          withReactionCounts: true,
          reactionKindsFilter: 'comment',
          enrich: true,
        })

        const count =
          query.results.length > 0 && (query.results[0] as any).reaction_counts
            ? ((query.results[0] as any).reaction_counts.comment as number)
            : 0

        const resp = await GetVideoComments(profile?.address ?? '', video.activity_id, lastID)
        const users = await fbEnrichUsersFromIds(resp.comments.map((comment) => comment.user_id))

        const commentData = resp.comments
          ? resp.comments.map((comment) => {
              return {
                user: users.find((user) => user.address === comment.user_id),
                id: comment.id,
                created_at: new Date(comment.created_at * 1000),
                content: comment.content,
                replies_count: comment.replies_count,
                liked: comment.liked_reaction_id === '' ? undefined : comment.liked_reaction_id,
                own: profile?.address === comment.user_id,
                likes_count: comment.likes_count,
              }
            })
          : []

        return {
          items: commentData as EnrichedCommentData[],
          count: count,
          nextID: commentData.length > 0 ? commentData[commentData.length - 1].id : '',
        }
      } catch (err) {
        captureException(err)
        return {
          items: [],
          count: 0,
          nextID: '',
        }
      }
    },
    [profile?.address, streamClient, video.activity_id, video.created_by]
  )

  const {
    items,
    hasNext,
    loadNext,
    loadMoreRef,
    refresh,
    newItem,
    likeItem,
    unlikeItem,
    deleteItem,
  } = useInfiniteLoadComment<EnrichedCommentData>({
    id: video.address,
    getItems: getComments,
    loadingType: LoadingType.INFINITE,
  })

  React.useEffect(() => {
    if (index === currentIndex) {
      refresh()
    }
  }, [currentIndex, index])

  React.useEffect(() => {
    if (index === currentIndex) {
      updateHeight()
    }
  }, [items])

  const submit = async (comment: string, comment_id?: string): Promise<void> => {
    if (comment_id) {
      return
    } else {
      const resp = await CreateComment(video.created_by, video.activity_id, comment)
      const users = await fbEnrichUsersFromIds([resp.comment.user_id])
      newItem({
        user: users[0],
        id: resp.comment.id,
        created_at: new Date(resp.comment.created_at * 1000),
        content: resp.comment.content,
        replies_count: resp.comment.replies_count,
        liked: resp.comment.liked_reaction_id === '' ? undefined : resp.comment.liked_reaction_id,
        own: profile?.address === resp.comment.user_id,
        likes_count: resp.comment.likes_count,
      })
    }
  }

  const like = async (comment_id: string, reply_id?: string): Promise<void> => {
    if (reply_id) {
      return
    }

    const like = await LikeComment(video.created_by, video.activity_id, comment_id)
    likeItem(comment_id, like.liked_reaction_id, like.likes)
  }

  const unlike = async (comment_id: string, like_id: string, reply_id?: string): Promise<void> => {
    if (reply_id) {
      return
    }

    await UnlikeComment(video.created_by, video.activity_id, comment_id, like_id)
    unlikeItem(comment_id)
  }

  const deleteComment = async (comment_id: string, reply_id?: string): Promise<void> => {
    if (reply_id) {
      return
    }

    await DeleteComment(video.created_by, video.activity_id, comment_id)
    deleteItem(comment_id)
  }

  React.useEffect(() => {
    refresh()
    const section = document.getElementById('comment-section')
    const y = section?.getBoundingClientRect().top
    if (y && (document.documentElement.scrollTop >= y || document.body.scrollTop >= y)) {
      document.body.scrollTop = document.documentElement.scrollTop = 0
    }
  }, [profile])

  return (
    <OuterDiv id="comment-section">
      <NewComment video={video} submit={submit} typing={updateHeight} />
      {items
        .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())
        .map((comment: EnrichedCommentData, index) => (
          <Comment
            updateHeight={updateHeight}
            video={video}
            comment={comment}
            hideReply={false}
            like={like}
            unlike={unlike}
            deleteComment={deleteComment}
            key={`comment-${index}`}
          />
        ))}
      {hasNext && (
        <MoreCommentsDiv ref={loadMoreRef} onClick={() => loadNext()}>
          <ThemeProvider theme={theme}>
            <ExpandMoreRounded
              htmlColor="#D5D4FB"
              style={{
                width: 35,
                height: 35,
                marginLeft: `-5px`,
              }}
            />
          </ThemeProvider>
          <Action style={{ color: '#D5D4FB' }}>{`Show More Comments`}</Action>
        </MoreCommentsDiv>
      )}
    </OuterDiv>
  )
}

export default CommentsDetails
